import { Component, Inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { NgxFeatureFlagService } from 'ngx/go-modules/src/services/feature-flag/feature-flag.service';
import { rubricEditorModalToken } from 'go-modules/modals/rubric-editor/modal.factory';
import { mediaPreviewToken } from 'go-modules/modals/media-preview/modal.factory';
import { mediaToken } from 'go-modules/models/media/media.factory';
import { PreviewMarkerSetDialogComponent } from '../dialogs/preview-marker-set-dialog/preview-marker-set-dialog.component';
import { ActivityInstructionModel } from 'go-modules/models/activity';
import { LibraryService } from 'ngx/go-modules/src/services/library/library.service';
import type { Marker, MarkerSet } from 'ngx/go-modules/src/interfaces/markers/marker';
import type { Session } from 'ngx/go-modules/src/services/session-list-datasource/session-list.datasource';
import { ActivityTemplateModelType } from 'go-modules/models/activity-template/activity-template.factory';
import type { ActivityModel } from 'go-modules/models/activity/activity.schema';

@Component({
	selector: 'activity-info',
	template: require('./activity-info.component.html'),
	styles: [require('./activity-info.component.scss')]
})
export class ActivityInfoComponent implements OnInit {
	@Input() public activity: ActivityModel;
	@Input() public hideSourceMedia: boolean;
	@Input() public hideRecordingInstructions: boolean;
	@Input() public hideFeedbackInstructions: boolean;
	@Input() public isOnDashboard: boolean;
	@Input() public session: Session;

	public markerSet: MarkerSet;
	public aiMarkers: Marker[];

	constructor (
		private translate: TranslateService,
		private featureFlag: NgxFeatureFlagService,
		private dialog: MatDialog,
		private libraryService: LibraryService,
		@Inject(mediaToken) public MediaModel,
		@Inject(rubricEditorModalToken) private rubricEditorModal,
		@Inject(mediaPreviewToken) private mediaPreviewModal
	) {}

	public ngOnInit () {
		this.determineSelectedMarkerSet();

		if (this.featureFlag.isAvailable('LLM_TASKS') && this.activity.llm_definitions) {
			this.aiMarkers = this.activity.llm_definitions.map((definition) => {
				return definition.tags[0]; // first tag from each definition
			}) as Marker[];
		} else {
			this.aiMarkers = this.activity.ai_prompts.map((prompt) => {
				return prompt.tag;
			}) as Marker[];
		}
	}

	public somethingToShow (): boolean {
		return this.showSourceMedia()
			|| this.showInstructions(ActivityInstructionModel.TYPE.RECORDING)
			|| this.showInstructions(ActivityInstructionModel.TYPE.FEEDBACK)
			|| this.showAttachments();
	}

	public showSourceMedia (): boolean {
		return this.activity.source_media &&
			this.activity.source_media.media_id > 0 &&
			!this.hideSourceMedia;
	}

	public showInstructions (type: string): boolean {
		if (type === ActivityInstructionModel.TYPE.RECORDING) {
			return this.activity.hasRecordingInstructions() && !this.hideRecordingInstructions;
		} else if (type === ActivityInstructionModel.TYPE.FEEDBACK) {
			return this.activity.hasFeedbackInstructions() &&
				!this.hideFeedbackInstructions &&
				this.session &&
				this.session.isRecorded();
		}
	}

	public showAttachments (): boolean {
		return this.activity.attachments.length > 0;
	}

	public shouldShowActivityPicture (): boolean {
		return !this.activity.isConversationBoard();
	}

	public showActivityPicture (): void {
		const media = this.MediaModel.model({
			media_status: this.MediaModel.READY,
			media_type: this.MediaModel.TYPE.DOCUMENT,
			filename: 'fake_name.jpg'
		});
		switch (this.activity.template.type) {
			case ActivityTemplateModelType.STIMULUS_RESPONSE:
				media.media_url = 'https://staticassets.goreact.com/stimulus.png';
				media.title = this.translate.instant('activity-info_example-stimulus');
				break;
			case ActivityTemplateModelType.FEEDBACK_ONLY:
				media.media_url = 'https://staticassets.goreact.com/commentonly.png';
				media.title = this.translate.instant('activity-info_example-comment-only');
				break;
			default:
				media.media_url = 'https://staticassets.goreact.com/standard.png';
				media.title = this.translate.instant('activity-info_example-standard');
		}

		const modalData = {
			media,
			edit: false,
			allowDownload: false
		};

		this.mediaPreviewModal.open({
			modalData
		});
	}

	public getActivityTypeName (): string {
		if (this.activity.is_conversation && this.activity.has_response_media) {
			return this.activity.live_session_enabled
				? this.translate.instant('common_standard-group-with-live-review')
				: this.translate.instant('common_standard-group');
		} else if (this.activity.live_session_enabled) {
			if (this.activity.template.slug === ActivityTemplateModelType.STIMULUS_RESPONSE) {
				// Live stimulus
				return this.translate.instant('common_stimulus-with-live-review');
			}
			return this.translate.instant('common_standard-with-live-review');
		} else {
			return this.activity.template?.type;
		}
	}

	public isRubricEnabled (): boolean {
		return this.activity.isRubricEnabled() || this.activity.isPeerRubricEnabled();
	}

	public previewRubric (): void {
		const modalData = {
			options: {
				schemaId: this.activity.rubric_template_id || this.activity.peer_rubric_id,
				mode: 'print',
				previewOnly: true
			}
		};

		this.rubricEditorModal.open({
			modalData
		});
	}

	public previewMarkerSet (): void {
		this.dialog.open(PreviewMarkerSetDialogComponent, {data: {
			markerSet: this.markerSet,
			withName: true
		}});
	}

	public previewAiMarkers (): void {
		this.dialog.open(PreviewMarkerSetDialogComponent, {data: {
			markerSet: {
				tags: this.aiMarkers
			},
			aiStyle: true,
			withName: true
		}});
	}

	public shouldSeePreviewAiMarkers (): boolean {
		return (this.featureFlag.isAvailable('LLM_TASKS') && this.activity.llm_definitions && this.activity.llm_definitions.length > 0)
			|| (!this.featureFlag.isAvailable('LLM_TASKS') && this.activity.ai_prompts && this.activity.ai_prompts.length > 0);
	}

	private determineSelectedMarkerSet (): void {
		if (this.activity.tag_set_id > 0) {
			this.libraryService.getMarkerSet(this.activity.tag_set_id).subscribe((markerSet) => {
				this.markerSet = markerSet;
			});
		}
	}
}
